# Janabel Xia

**Email:** [janabeltxia@gmail.com](mailto:janabeltxia@gmail.com) | **LinkedIn:** [linkedin.com/in/janabel-xia](https://www.linkedin.com/in/janabel-xia-21099711a) | **GitHub:** [github.com/janabel](https://github.com/janabel)

---

## Education and Relevant Coursework (\*Graduate)

**Harvard University**, Cambridge, MA  
_PhD in Mathematics_  
_Sep. 2025 -- Present_

**Massachusetts Institute of Technology (MIT)**, Cambridge, MA  
_Bachelor of Science in Mathematics, GPA 5.0/5.0_  
_Sep. 2020 -- May 2024_

- **Math**: Abstract Algebra, Real Analysis, Nonlinear Dynamics, Continuum Modelling, Discrete Math Seminar, Information Theory Seminar, Complexity Theory\*, Algebraic Extremal Combinatorics\*, Theoretical Cryptography\*, Graph Theory and Additive Combinatorics\*
- **CS**: Algorithms I, Algorithms II, Machine Learning, Fundamentals of Programming, Interactive Music Systems
- **Urban Studies and Sociology**: Land Use Law and Politics\*, Writing About Race, Intro to Political Philosophy, Intro to Housing, Community and Economic Development, Community-Informed Urban Design (Harvard SES 5460)
- **Brain and Cognitive Sciences**: Neural Circuits, The Human Brain, Child Development

---

## Publications and Preprints

- **Liam Eagen, Hy Ngo, Vikas Rushi, Ying Tong, Moven Tsai, and Janabel Xia**, _OpenAC: Open Design for Transparent and Lightweight Anonymous Credentials_, Cryptology ePrint Archive, Paper 2026/251 (2026), [eprint.iacr.org/2026/251](https://eprint.iacr.org/2026/251) (preprint)
- **Janabel Xia**, _Deterministic Stack-Sorting for Set Partitions_, ECA **4** (2024), no. 3, Article S2R23 [doi.org/10.54550/ECA2024V4S3R23](https://doi.org/10.54550/ECA2024V4S3R23)
- **Ethan Pesikoff, Benjamin Przybocki, and Janabel Xia**, _The Maximum Hook Length of $d$-Distinct Simultaneous Core Partitions_, [Electron. J. Combin. 30](https://www.combinatorics.org/) (2023), no. 3, Paper No. 3.30, [doi.org/10.37236/11553](https://doi.org/10.37236/11553)
- **Judy Hsin-Hui Chiang, Anh Trong Nam Hoang, Matthew Kendall, Ryan Lynch, Son Nguyen, Benjamin Przybocki, and Janabel Xia**, _Bender-Knuth involutions on linear extensions of posets_, Discrete Mathematics **347** (2024), no. 9, 114068, [doi.org/10.1016/j.disc.2024.114068](https://doi.org/10.1016/j.disc.2024.114068)
- **Tyler Beauregard, Janabel Xia, and Mike Rosulek**, _Finding one common item, privately_, Security and cryptography for networks, Lecture Notes in Comput. Sci., vol. 13409, Springer, Cham, [2022], pp. 462–480, [doi.org/10.1007/978-3-031-14791-3_20](https://doi.org/10.1007/978-3-031-14791-3_20)

---

## Academic Research Experience

- **UMN Duluth Combinatorics REU**, Research Experience for Undergraduates (REU) sponsored by Jane Street Capital, University of Minnesota Duluth  
  _June 2023 -- August 2023_
  - Conducted combinatorics research under Colin Defant and Noah Kravitz. Investigated stack-sorting unordered sequences, improving bounds for minimum stack numbers, resulting in a solo publication listed above.

- **UMN Twin Cities Combinatorics and Algebra REU**, National Science Foundation, Research Experience for Undergraduates (REU), University of Minnesota Twin Cities  
  _June 2022 -- August 2022_
  - Conducted combinatorics research on group actions on linear extensions under Professor Vic Reiner and simultaneous core partitions under Hannah Burson, resulting in two publications listed above. Tested conjectures with Python using SageMath software.

- **Cryptography and Information Security Group**, MIT CSAIL  
  _January 2022 -- May 2022_
  - Conducted cryptanalysis research under Professor Vinod Vaikuntanathan. Studied subspace-trail-based distinguishers (the "mod-8 attack") on the AES block cipher.

- **Oregon State University Math and Theoretical CS REU**, National Science Foundation, Research Experience for Undergraduates (REU), Oregon State University  
  _June 2021 -- August 2021_
  - Conducted cryptography research under Professor Mike Rosulek. Worked on secure Diffie-Hellman Private Set Intersection protocols, resulting in a publication at the SCN 2022 international conference.

---

## Awards and Achievements

- **James Mills Peirce Fellowship 2025-2026**, Jan. 2025  
  On recommendation of the math department, received as a PhD student in the natural sciences an engineering.

- **National Science Foundation Graduate Research Fellowship Program Recipient**, Apr. 2024  
  Received the NSF GRFP as one of 83 mathematical sciences students.

- **Invitation to the XI Chapter of Phi Beta Kappa Society**, Apr. 2024  
  Invited to be a member of Phi Beta Kappa, the oldest academic honor society in liberal arts and sciences, while an undergrad at MIT.

- **European Girls' Math Olympiad (EGMO) Individual Silver Medalist**, Apr. 2019  
  Competed internationally as 1 of 4 members on the 1st place US Team.

- **Two-time Math Olympiad Program (MOP) Attendee**, June 2018, June 2019  
  Attended the Math Olympiad training camp for top 60 high school students in the US.

---

## Work Experience

- **Community Privacy Residency**  
  Co-founder  
  _November 2024 -- Present_  
  _Remote & Taipei, Taiwan_
  - Co-founded the [Community Privacy Residency](https://community-privacy.github.io/), a 2-4 week residency bringing together 43 cryptographers, developers, designers, community organizers and activists from 15 countries to research, co-design, and build privacy infrastructure. Applied for grants, booked physical spaces, designed programming and community events, and led finances and operations in the span of late November to late February. Ran first residency from February - March 2025.

- **zkID Research and Standards**  
  Researcher  
  _May 2025 -- September 2025_  
  _Remote_
  - Worked on [ZK Standards efforts](https://hackmd.io/@janabel/B1bgGDYbxl) and conducted research, architecture design, and writing for [zkID](https://github.com/privacy-ethereum/zkID), an [initiative](https://pse.dev/projects/zk-id) by the Ethereum Foundation to promote the adoption of privacy layers around government-issued digital ID systems, such as the EUDI, and more.

- **0xPARC Foundation**  
  Research and Development Contractor  
  _July 2024 -- August 2024, January -- February 2025_  
  _San Francisco & Remote_
  - Wrote [blog](https://notes.0xparc.org/notes/collab-snark/) [posts](https://notes.0xparc.org/notes/mpc-fhe-key-switching/) to distill hard mathematical concepts into a form accessible to technical generalists. Developed interactive educational demos for key cryptographic primitives, as well as proof-of-concept zero-knowledge application [POD counting](https://github.com/janabel/frog-counting). Investigated distributed and collaborative proving systems, allowing users to jointly and verifiably compute on private data.

- **Mathematical Olympiad Program (MOP) Teaching Assistant and Residential Assistant**  
  Mathematical Association of America  
  _July 2020, June 2021, June 2022_  
  _Carnegie Mellon University_
  - As Teaching Assistant, graded student work on Mock Olympiad tests held every other day of the program. Developed detailed rubrics for grading, and presented problem solutions during test review. As Residential Assistant, advocated for the existence of this role to create a socially meaningful experience during virtual MOP in 2020. Created community engagement structures, moderated messages, and held small group and 1-1 check-ins to monitor student well-being.

---

## Volunteering and Leadership

- **Justice 4 Housing**  
   Intern  
   _Sept. 2024 -- February 2025_  
   _Boston, MA_
  - Volunteered with [Justice 4 Housing](https://justice4housing.org/) in their Hands On Defense (HOD) program under Naia Wilson. Strengthened
    data infrastructure by cleaning, organizing, and migrating case data; enhanced the nonprofit’s capacity to manage
    housing support for formerly incarcerated individuals.

- **Active Community Engagement Freshman Pre-Orientation Program**  
  Coordinator, Counselor  
  _Dec. 2020 -- August 2023_  
  _MIT_
  - As a coordinator, directed all programming and managed counselors in addition to counselor duties. As a counselor, led social justice workshops, supervised community service placements, organized community-building events like Boston walking tours, and facilitated sensitive discussions based on year-round training.

- **MIT Asian Dance Team (ADT)**  
  Director of Public Relations, Head Choreographer, Choreographer  
  _January 2020 -- May 2024_  
  _MIT_
  - As the director of public relations, led external communications and publicized events. As a head choreographer, provided mentorship and resources to other choreographers. As a current choreographer, teach and direct group dances in semesterly showcases.

- **MIT Fixation**  
  Artistic Director, Captain  
  _September 2021 -- December 2023_  
  _MIT_
  - As an artistic director for MIT's competitive contemporary dance team, choreographed and directed the team set pieces. As the captain, led warmups and organized team bonding.

- **Chinese American Association of Lexington (CAAL)**  
  CAAL CARING Data Team Lead  
  _March 2020 -- June 2020_  
  _Lexington, MA_
  - Served as the data team lead for CAAL's COVID-19 Mask Donation Project, which donated over 190,000 PPE items to hospitals, nursing homes, and public centers in the New England area. Developed automated Google Sheets architecture for data processing; reported donation statistics and graph visualizations, used externally by CAAL.

## Projects

- **Frog Counting**  
  _August 2024 - October 2024_
  - Working on proof-of-concept websites that allow users to i) prove ownership of many (frog) PODs (an interoperable cryptographic data type) and ii) get issued a corresponding POD signing this statement of ownership. Uses the [Sonobe](https://github.com/privacy-scaling-explorations/sonobe) folding schemes library. [GitHub](https://github.com/janabel/frog-counting)

- **zkPoll Private Polling dApp**  
  _January 2023_
  - Created [zkpoll.xyz](https://zkpoll.xyz/), an end-to-end private polling site deployed on the Ethereum blockchain. Wrote zkSNARK circuits in the Circom programming language to enable anonymous proofs of membership in voter sets with the Groth16 proving system. [GitHub](https://github.com/zk-poll/zk-poll).

- **VHLApp Android Mobile App**  
  _January 2021_
  - Coded app prototype from scratch in Java for healthcare nonprofit VHL Alliance. App allows users to access medical information and organize medical contacts. Used the Backendless API and database platform.

## Skills

- **Languages/Libraries**: Python (NumPy, Matpotlib), TypeScript/JavaScript, HTML/CSS, Circom
- **Software**: Jupyter Notebook, SageMath, GitHub
- **Developer Tools**: Git, VSCode, React, Parcel, Vite, Vercel

---
